import { AML_LEVEL_STANDARD, BUSINESS_LIMIT, LIMIT_TYPE_COUNT, THREE_MONTHLY_LIMITS } from 'apps/auth/constants/Constants';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import { formatMoney } from 'apps/auth/helpers/formats';
import { getLimitEndDate } from 'apps/auth/helpers/utils';
import { UsaService, type UsaProfile } from 'apps/auth/services/UsaService';

export type LimitGroupEntry = {
    cycle?: string;
    type?: string;
    used?: number | string;
    max?: number | string;
    left?: number;
    limitSubType?: string;
    formatedMax?: string;
    formatedLeft?: string;
    totalLimitEnd?: string;
    [key: string]: unknown;
};

export type ProfileWithLimits = {
    limitsGroup?: LimitGroupEntry[];
    limits?: {
        left?: number;
    };
    amlLimit?: {
        currentLimit?: number;
    };
    threeMonthLimitEnabled?: boolean;
} & UsaProfile;

export type CommissionInfo = {
    convertRate?: number | string;
};

export type AdditionalLimit = {
    left?: number;
};

type ThreeMonthCounterResult = {
    counter: LimitGroupEntry | null;
    isExceedNumberTransactions: boolean;
};

type ThreeMonthLimitsResult = {
    hasThreeMonthLimitErrors: boolean;
    isExceedNumberTransactions: boolean;
    isZeroRemainingProfileLimits: boolean;
};

type UsaLimitsErrorResult = {
    isTransactionLimitUsa: boolean;
    minSumProfileLimitFrom: number | false;
    minSumProfileLimit: number | false | '';
    hasUsaLimitErrors: boolean;
    errorLimits: string;
    errorMessageMaxLimit: string;
    errorMessageLeftLimit: string;
    dateEnd: string;
};

type BusinessLimitResult = LimitGroupEntry | Record<string, never>;

const toNumber = (value: number | string | undefined): number => {
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
};

export class LimitsService {
    static findThreeMonthCounter(canMakeDraftTransaction: boolean | undefined, isSend: boolean, profileForm: ProfileWithLimits = {}): ThreeMonthCounterResult {
        if (Array.isArray(profileForm?.limitsGroup)) {
            const counter = profileForm.limitsGroup.find((limit) => limit?.cycle === THREE_MONTHLY_LIMITS && limit?.type === LIMIT_TYPE_COUNT) ?? null;
            if (counter && Object.keys(counter).length > 0) {
                const needToDisableError = canMakeDraftTransaction && isSend;
                const isExceedNumberTransactions = Number(counter?.used) === Number(counter?.max) && !needToDisableError;
                return { counter, isExceedNumberTransactions };
            }
        }
        return { counter: null, isExceedNumberTransactions: false };
    }

    static getZeroProfileLimitsErrors(value: number | string, canMakeDraftTransaction: boolean | undefined, profile?: ProfileWithLimits): boolean {
        if (!canMakeDraftTransaction) {
            const limit = profile?.limits?.left ?? 0;
            const numericValue = toNumber(value);
            return limit === 0 && numericValue > limit;
        }
        return false;
    }

    static getZeroAdditionalLimitsErrors(value: number | string, additionalLimit?: AdditionalLimit | null): boolean {
        const limit = additionalLimit?.left ?? 0;
        const numericValue = toNumber(value);
        return limit === 0 && numericValue > limit;
    }

    static isAdditionalLimitError(value: number, isFrom: boolean, commission: CommissionInfo = {}, additionalLimit?: AdditionalLimit | null): boolean {
        const convertRate = commission?.convertRate !== undefined ? Number(commission.convertRate) : null;

        if (!isFrom && convertRate && typeof convertRate === 'number' && additionalLimit?.left) {
            const leftInToCurrency = Number((additionalLimit.left * convertRate).toFixed(2));
            return value > leftInToCurrency;
        }

        return Boolean(additionalLimit?.left && value > additionalLimit.left);
    }

    static getThreeMonthLimitsError(
        params: { value: number; canMakeDraftTransaction: boolean | undefined } & Record<string, unknown>,
        profile: ProfileWithLimits = {},
    ): ThreeMonthLimitsResult {
        const { value, canMakeDraftTransaction } = params;
        const isNoIdent = (profile?.amlLimit?.currentLimit ?? 0) < AML_LEVEL_STANDARD;
        if (profile?.threeMonthLimitEnabled && isNoIdent) {
            const { isExceedNumberTransactions } = this.findThreeMonthCounter(canMakeDraftTransaction, true, profile);
            const isZeroRemainingProfileLimits = this.getZeroProfileLimitsErrors(value, canMakeDraftTransaction, profile);
            const hasThreeMonthLimitErrors = isExceedNumberTransactions || isZeroRemainingProfileLimits;
            return { hasThreeMonthLimitErrors, isExceedNumberTransactions, isZeroRemainingProfileLimits };
        }
        return {
            hasThreeMonthLimitErrors: false,
            isExceedNumberTransactions: false,
            isZeroRemainingProfileLimits: false,
        };
    }

    static getUsaLimitsErrors(
        value: number,
        maxAmount: number,
        countryId: string | number | undefined,
        currencyCode: string,
        isFrom: boolean,
        commission: CommissionInfo = {},
        profileForm: ProfileWithLimits = {},
        lang = 'en',
    ): UsaLimitsErrorResult {
        const isUsaLimits = countryId === USA && UsaService.isNewLimits(profileForm);
        if (isUsaLimits && Array.isArray(profileForm?.limitsGroup) && profileForm.limitsGroup.length > 0) {
            const convertRate = commission?.convertRate !== undefined ? Number(commission.convertRate) : null;
            const limits = profileForm.limitsGroup.map((limit) => {
                if (!isFrom && convertRate && typeof convertRate === 'number' && limit?.left) {
                    return {
                        ...limit,
                        left: Number((limit.left * convertRate).toFixed(2)),
                    };
                }
                return limit;
            });
            limits.push({
                left: maxAmount,
                cycle: 'isTransactionalLimits',
                formatedMax: '',
                formatedLeft: '',
                totalLimitEnd: '',
            });
            const minSumLimit = Math.min(
                ...limits.map((limit) => {
                    const numericLeft = typeof limit.left === 'number' ? limit.left : Number(limit.left);
                    return Number.isFinite(numericLeft) ? numericLeft : Number.POSITIVE_INFINITY;
                }),
            );
            const limitObj = limits.find((limit) => (limit.left ?? Number.POSITIVE_INFINITY) === minSumLimit);
            const minSumToFormatted = limitObj ? formatMoney(limitObj.left ?? null) : undefined;
            const isTransactionLimitUsa = limitObj?.cycle === 'isTransactionalLimits';
            const dateEnd = limitObj?.totalLimitEnd ? getLimitEndDate(limitObj.totalLimitEnd, lang) : '';
            const limitFrom = !isTransactionLimitUsa ? profileForm?.limitsGroup?.find((limit) => limit.cycle === limitObj?.cycle) : undefined;
            const leftLimitMessage = isFrom && !isTransactionLimitUsa ? limitObj?.formatedLeft : minSumToFormatted;
            return {
                isTransactionLimitUsa,
                minSumProfileLimitFrom: !isTransactionLimitUsa ? (limitFrom?.left ?? false) : false,
                minSumProfileLimit: !isTransactionLimitUsa && minSumLimit,
                hasUsaLimitErrors: value > minSumLimit,
                errorLimits: limitObj?.cycle ?? '',
                errorMessageMaxLimit: `${limitObj?.formatedMax} ${currencyCode}`,
                errorMessageLeftLimit: `${leftLimitMessage} ${currencyCode}`,
                dateEnd,
            };
        }
        return {
            isTransactionLimitUsa: false,
            minSumProfileLimitFrom: false,
            minSumProfileLimit: '',
            hasUsaLimitErrors: false,
            errorLimits: '',
            errorMessageMaxLimit: '',
            errorMessageLeftLimit: '',
            dateEnd: '',
        };
    }

    static findBusinessLimits(profile: ProfileWithLimits): BusinessLimitResult {
        if (Array.isArray(profile?.limitsGroup) && profile.limitsGroup.length > 1) {
            return profile.limitsGroup.find((limit) => limit?.limitSubType === BUSINESS_LIMIT) || {};
        }
        return {};
    }
}
